import { Brain } from 'lucide-react';
import BackToHome from './BackToHome';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <BackToHome />
            <div className="flex items-center gap-2">
              <div className="bg-[#8CAFA8] text-white p-2 rounded-lg">
                <Brain className="w-5 h-5" />
              </div>
              <span className="text-xl font-semibold text-[#1F2937]">Mentra</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-white rounded-[24px] shadow-sm p-8 md:p-12">
          <h1 className="text-4xl font-semibold text-[#1F2937] mb-2">Privacy Policy</h1>
          <p className="text-[#6B7280] mb-8">Last Updated: 18 November 2025</p>

          <div className="prose prose-gray max-w-none">
            <p className="text-lg leading-relaxed mb-6">
              Mentra ("we," "us," "our") provides a cloud-based platform designed to help teams give and receive feedback, improve performance, and support personal growth. We comply with the New Zealand Privacy Act 2020 ("the Act") when handling personal information.
            </p>
            <p className="leading-relaxed mb-6">
              This policy explains how we collect, use, disclose, and protect your personal information.
            </p>
            <p className="leading-relaxed mb-8">
              This policy does not limit or exclude any of your rights under the Act. For more details, visit{' '}
              <a href="https://www.privacy.org.nz" target="_blank" rel="noopener noreferrer" className="text-[#8CAFA8] hover:text-[#7A9B94]">
                www.privacy.org.nz
              </a>
            </p>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">1. Changes to This Policy</h2>
              <p className="leading-relaxed mb-3">We may update this Privacy Policy at any time by posting a revised version on our website.</p>
              <p className="leading-relaxed">Any changes take effect from the date of posting.</p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">2. Information We Collect</h2>
              <p className="leading-relaxed mb-4">We collect personal information to provide our service, keep it secure, and continuously improve it.</p>

              <h3 className="text-xl font-semibold text-[#1F2937] mb-3 mt-6">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Account details (name, email address, password)</li>
                <li>Profile information (role, department, optional bio or avatar)</li>
                <li>Feedback and review content (ratings, comments, peer feedback, goals, coaching notes)</li>
                <li>Messages sent to our support team</li>
                <li>Any information submitted through our website, app, or registration processes</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#1F2937] mb-3 mt-6">2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Device and browser data</li>
                <li>IP address and log information</li>
                <li>Pages viewed, session duration</li>
                <li>Usage analytics (features used, interactions, timestamps)</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#1F2937] mb-3 mt-6">2.3 Information From Third Parties</h3>
              <p className="leading-relaxed mb-3">We may collect information from:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Your organisation (for managed accounts)</li>
                <li>Publicly available sources</li>
                <li>Third parties where authorised by you</li>
                <li>Single sign-on providers (Google, Microsoft, etc.) — we do not receive your SSO password</li>
              </ul>
              <p className="leading-relaxed">Where possible, we collect information directly from you.</p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">3. How We Use Your Personal Information</h2>
              <p className="leading-relaxed mb-3">We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Verify identity and manage accounts</li>
                <li>Provide and improve our products and services</li>
                <li>Deliver personalised feedback insights and performance dashboards</li>
                <li>Contact you about updates, support, onboarding, and marketing (you can opt out anytime)</li>
                <li>Respond to your requests or complaints</li>
                <li>Conduct research and statistical analysis (anonymised)</li>
                <li>Bill you and process payments</li>
                <li>Undertake credit checks (if required)</li>
                <li>Protect and enforce our legal rights</li>
                <li>Meet legal and regulatory requirements</li>
                <li>Any other purpose authorised by you or permitted under the Act</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#1F2937] mb-3 mt-6">Use of OpenAI</h3>
              <p className="leading-relaxed mb-3">Feedback data may be shared with OpenAI solely for the purpose of:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>formatting comments,</li>
                <li>generating summaries,</li>
                <li>assisting in report creation.</li>
              </ul>
              <p className="leading-relaxed mb-3">Data is transferred under strong contractual and security controls.</p>
              <p className="leading-relaxed font-semibold">We do not sell personal information.</p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">4. Disclosing Your Personal Information</h2>
              <p className="leading-relaxed mb-3">We may disclose your personal information to:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Other companies within our group</li>
                <li>Service providers that support our platform (hosting, analytics, email providers, etc.)</li>
                <li>Payment processors</li>
                <li>Credit agencies (only if credit checking is required)</li>
                <li>Third parties for anonymised, aggregated statistical reporting</li>
                <li>Regulatory authorities or law enforcement, where required</li>
                <li>Any person authorised by you</li>
                <li>Any person authorised by the Act or other applicable laws</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#1F2937] mb-3 mt-6">OpenAI Disclosure</h3>
              <p className="leading-relaxed mb-6">
                As noted, anonymised or user-provided feedback data may be shared with OpenAI to generate summaries or reports.
              </p>

              <h3 className="text-xl font-semibold text-[#1F2937] mb-3 mt-6">International Transfers</h3>
              <p className="leading-relaxed mb-3">Some of our service providers may store or process personal data outside New Zealand.</p>
              <p className="leading-relaxed mb-3">Our primary hosting locations include:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Sydney, Australia</li>
                <li>San Francisco, USA</li>
              </ul>
              <p className="leading-relaxed mb-6">
                We take reasonable steps to ensure overseas providers comply with privacy and security obligations.
              </p>

              <h3 className="text-xl font-semibold text-[#1F2937] mb-3 mt-6">Business Transactions</h3>
              <p className="leading-relaxed">
                If we undergo a merger, sale, investment, or reorganisation, your information may be transferred as part of that process.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">5. Protecting Your Personal Information</h2>
              <p className="leading-relaxed mb-3">
                We take reasonable steps to protect your information from loss, unauthorised access, modification, or misuse, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Encryption in transit and at rest</li>
                <li>Access controls and authentication</li>
                <li>Secure data hosting</li>
                <li>Regular security audits</li>
                <li>Monitoring and logging</li>
              </ul>
              <p className="leading-relaxed">
                Despite strong safeguards, no system is 100% secure—and we are transparent about that.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">6. Accessing and Correcting Your Personal Information</h2>
              <p className="leading-relaxed mb-3">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Access personal information we hold about you</li>
                <li>Request corrections to that information</li>
              </ul>
              <p className="leading-relaxed mb-3">
                We may need to verify your identity before granting access or making changes.
              </p>
              <p className="leading-relaxed mb-3">
                If we decline to correct information, we will take reasonable steps to note that you requested the correction.
              </p>
              <p className="leading-relaxed mb-3">To exercise these rights, email:</p>
              <p className="font-semibold mb-6">
                <a href="mailto:contact@mentra.com" className="text-[#8CAFA8] hover:text-[#7A9B94]">
                  contact@mentra.com
                </a>
              </p>
              <p className="leading-relaxed">
                We may charge reasonable costs for providing access to copies of your information.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">7. Data Retention</h2>
              <p className="leading-relaxed mb-3">We retain personal information:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>For as long as your account remains active</li>
                <li>As long as is reasonably required to provide the service</li>
                <li>As required by your organisation (for managed accounts)</li>
                <li>As required to comply with legal or financial obligations</li>
              </ul>
              <p className="leading-relaxed">
                You may request deletion of your account and data at any time.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">8. Internet Use</h2>
              <p className="leading-relaxed mb-3">
                While we take reasonable steps to maintain secure internet connections, transmitting personal information over the internet is done at your own risk.
              </p>
              <p className="leading-relaxed">
                If you follow links from our platform to external sites, their privacy standards apply—not ours.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">9. Cookies</h2>
              <p className="leading-relaxed mb-3">We use cookies to:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Recognise your browser</li>
                <li>Maintain login sessions</li>
                <li>Analyse website usage</li>
                <li>Support functionality and performance</li>
                <li>Enable optional advertising/retargeting services</li>
              </ul>
              <p className="leading-relaxed">
                You may disable cookies in your browser, but some features of the site may not work properly.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">10. Children's Privacy</h2>
              <p className="leading-relaxed mb-3">Mentra is not intended for individuals under 16.</p>
              <p className="leading-relaxed">
                We do not knowingly collect personal data from children.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">11. Contact Us</h2>
              <p className="leading-relaxed mb-3">
                If you have questions, concerns, or requests relating to your personal information or this policy, contact:
              </p>
              <p className="font-semibold">
                <a href="mailto:team@getmentra.com" className="text-[#8CAFA8] hover:text-[#7A9B94]">
                  team@getmentra.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      <footer className="border-t border-gray-200 py-8 mt-12">
        <div className="container mx-auto px-6 text-center text-[#6B7280]">
          <p>&copy; 2025 Mentra. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
